import React, { useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { CODICI_TRIBUTO } from "../../config/fiscalConstants";
import {
  F24_SEZIONI,
  F24_CATEGORIE,
  createF24Riga,
  computeF24Totale,
} from "../../utils/f24Schema";

const CATEGORIE_OPTIONS = [
  { value: F24_CATEGORIE.ACCONTO_GIUGNO, label: "Acconto giugno" },
  { value: F24_CATEGORIE.ACCONTO_NOVEMBRE, label: "Acconto novembre" },
  { value: F24_CATEGORIE.SALDO, label: "Saldo" },
  { value: F24_CATEGORIE.BOLLO, label: "Bollo" },
  { value: F24_CATEGORIE.ALTRO, label: "Altro" },
];

const PRESET_RIGHE = [
  { label: "Acconto imposta", riga: { sezione: F24_SEZIONI.ERARIO, codiceTributo: CODICI_TRIBUTO.erario.acconto1 } },
  { label: "Saldo imposta", riga: { sezione: F24_SEZIONI.ERARIO, codiceTributo: CODICI_TRIBUTO.erario.saldo } },
  { label: "Acconto INPS", riga: { sezione: F24_SEZIONI.INPS, codiceTributo: CODICI_TRIBUTO.inps.unicaSoluzione } },
  { label: "Bollo", riga: { sezione: F24_SEZIONI.ERARIO, codiceTributo: CODICI_TRIBUTO.bollo } },
];

const formatEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

function categoriaLabel(value) {
  return CATEGORIE_OPTIONS.find((c) => c.value === value)?.label || "";
}

function getDefaultDescrizione(categoria, anno) {
  const label = categoriaLabel(categoria);
  return label && categoria !== F24_CATEGORIE.ALTRO ? `${label} ${anno}` : "";
}

// Riga in forma "editor": importi come stringhe per gli input; campi avanzati passthrough.
function toEditorRiga(r = {}) {
  const base = createF24Riga(r);
  return {
    sezione: base.sezione,
    codiceTributo: base.codiceTributo || "",
    importoDebito: base.importoDebito ? String(base.importoDebito) : "",
    importoCredito: base.importoCredito ? String(base.importoCredito) : "",
    rateazione: base.rateazione ?? null,
    periodoRiferimentoDa: base.periodoRiferimentoDa ?? null,
    periodoRiferimentoA: base.periodoRiferimentoA ?? null,
    codiceSede: base.codiceSede ?? null,
    causaleContributo: base.causaleContributo ?? null,
  };
}

function toSchemaRiga(er) {
  return createF24Riga({
    sezione: er.sezione,
    codiceTributo: (er.codiceTributo || "").trim(),
    importoDebito: Number(er.importoDebito) || 0,
    importoCredito: Number(er.importoCredito) || 0,
    rateazione: er.rateazione ?? null,
    periodoRiferimentoDa: er.periodoRiferimentoDa ?? null,
    periodoRiferimentoA: er.periodoRiferimentoA ?? null,
    codiceSede: er.codiceSede ?? null,
    causaleContributo: er.causaleContributo ?? null,
  });
}

export default function F24Modal({
  open,
  mode = "create",
  initialValues,
  year,
  codiceSedeInps = "",
  onClose,
  onSubmit,
  darkMode = false,
}) {
  const [categoria, setCategoria] = useState(F24_CATEGORIE.ACCONTO_GIUGNO);
  const [descrizione, setDescrizione] = useState("");
  const [descrizioneManual, setDescrizioneManual] = useState(false);
  const [dataScadenza, setDataScadenza] = useState("");
  const [anno, setAnno] = useState(year || new Date().getFullYear());
  const [note, setNote] = useState("");
  const [righe, setRighe] = useState([toEditorRiga({ sezione: F24_SEZIONI.ERARIO })]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const currentYear = year || new Date().getFullYear();
    if (initialValues) {
      const cat = initialValues.categoria || F24_CATEGORIE.ALTRO;
      setCategoria(cat);
      setDescrizione(initialValues.descrizione || "");
      setDescrizioneManual(true);
      setDataScadenza(initialValues.dataScadenza || "");
      setAnno(initialValues.annoRiferimento || initialValues.anno || currentYear);
      setNote(initialValues.note || "");
      const baseRighe = Array.isArray(initialValues.righe) && initialValues.righe.length
        ? initialValues.righe
        : [{ sezione: F24_SEZIONI.ERARIO, importoDebito: Number(initialValues.importo) || 0 }];
      setRighe(baseRighe.map(toEditorRiga));
    } else {
      setCategoria(F24_CATEGORIE.ACCONTO_GIUGNO);
      setDescrizione(getDefaultDescrizione(F24_CATEGORIE.ACCONTO_GIUGNO, currentYear));
      setDescrizioneManual(false);
      setDataScadenza("");
      setAnno(currentYear);
      setNote("");
      setRighe([toEditorRiga({ sezione: F24_SEZIONI.ERARIO })]);
    }
    setErrors({});
  }, [open, mode, initialValues, year]);

  const handleCategoriaChange = (value) => {
    setCategoria(value);
    if (!descrizioneManual) setDescrizione(getDefaultDescrizione(value, anno));
  };

  const handleAnnoChange = (value) => {
    const n = Number(value);
    setAnno(n);
    if (!descrizioneManual) setDescrizione(getDefaultDescrizione(categoria, n));
  };

  const updateRiga = (index, field, value) => {
    setRighe((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRiga = (preset) => {
    setRighe((prev) => [...prev, toEditorRiga(preset || { sezione: F24_SEZIONI.ERARIO })]);
  };

  const removeRiga = (index) => {
    setRighe((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const totale = computeF24Totale(righe.map(toSchemaRiga));

  const validate = () => {
    const errs = { righe: {} };
    if (!dataScadenza) errs.dataScadenza = "Data scadenza obbligatoria";
    if (totale <= 0) errs.totale = "Il totale deve essere maggiore di zero";
    righe.forEach((r, i) => {
      if (!(r.codiceTributo || "").trim()) errs.righe[i] = "Codice tributo obbligatorio";
    });
    const hasRigaError = Object.keys(errs.righe).length > 0;
    setErrors(errs);
    return !errs.dataScadenza && !errs.totale && !hasRigaError;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      categoria,
      descrizione: descrizione.trim() || getDefaultDescrizione(categoria, anno),
      dataScadenza,
      annoRiferimento: Number(anno),
      note: note.trim(),
      righe: righe.map(toSchemaRiga).map((r) =>
        r.sezione === F24_SEZIONI.INPS && !r.codiceSede && (codiceSedeInps || "").trim()
          ? { ...r, codiceSede: (codiceSedeInps || "").trim() }
          : r
      ),
      schemaVersion: 2,
    });
    onClose();
  };

  if (!open) return null;

  const bg = darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900";
  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm ${
    darkMode
      ? "bg-neutral-800 border-neutral-700 text-white placeholder-neutral-500"
      : "bg-white border-neutral-300 text-neutral-900"
  } focus:ring-2 focus:ring-forfy-500 focus:border-transparent`;
  const labelClass = `block text-xs font-medium mb-1 ${darkMode ? "text-neutral-400" : "text-neutral-600"}`;
  const rigaBox = darkMode ? "border-neutral-700 bg-neutral-800/50" : "border-neutral-200 bg-neutral-50";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`${bg} rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}>
          <h2 className="text-lg font-semibold">{mode === "edit" ? "Modifica F24" : mode === "duplicate" ? "Duplica F24" : "Nuovo F24"}</h2>
          <button onClick={onClose} className={`p-1 rounded-lg ${darkMode ? "hover:bg-neutral-800" : "hover:bg-neutral-100"}`}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Categoria */}
          <div>
            <label className={labelClass}>Categoria</label>
            <select value={categoria} onChange={(e) => handleCategoriaChange(e.target.value)} className={inputClass}>
              {CATEGORIE_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Descrizione */}
          <div>
            <label className={labelClass}>Descrizione</label>
            <input
              type="text"
              value={descrizione}
              onChange={(e) => { setDescrizione(e.target.value); setDescrizioneManual(true); }}
              placeholder="Es: Acconto giugno 2025"
              className={inputClass}
            />
          </div>

          {/* Scadenza + Anno */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Data scadenza *</label>
              <input
                type="date"
                value={dataScadenza}
                onChange={(e) => setDataScadenza(e.target.value)}
                className={`${inputClass} ${errors.dataScadenza ? "border-red-500" : ""}`}
              />
              {errors.dataScadenza && <p className="text-xs text-red-500 mt-1">{errors.dataScadenza}</p>}
            </div>
            <div>
              <label className={labelClass}>Anno di riferimento</label>
              <input type="number" min="2020" max="2040" value={anno} onChange={(e) => handleAnnoChange(e.target.value)} className={inputClass} />
            </div>
          </div>

          {/* Righe */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? "text-neutral-400" : "text-neutral-600"}`}>Righe tributo</label>
              <button type="button" onClick={() => addRiga()} className="inline-flex items-center gap-1 text-xs font-medium text-forfy-600 hover:text-forfy-700">
                <Plus size={14} /> Aggiungi riga
              </button>
            </div>

            <div className="space-y-3">
              {righe.map((r, i) => (
                <div key={i} className={`rounded-xl border p-3 ${rigaBox}`}>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className={labelClass}>Sezione</label>
                      <select value={r.sezione} onChange={(e) => updateRiga(i, "sezione", e.target.value)} className={inputClass}>
                        <option value={F24_SEZIONI.ERARIO}>Erario</option>
                        <option value={F24_SEZIONI.INPS}>INPS</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Codice tributo *</label>
                      <input
                        type="text"
                        value={r.codiceTributo}
                        onChange={(e) => updateRiga(i, "codiceTributo", e.target.value)}
                        placeholder="Es: 1790"
                        className={`${inputClass} ${errors.righe?.[i] ? "border-red-500" : ""}`}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 items-end">
                    <div>
                      <label className={labelClass}>Debito (€)</label>
                      <input type="number" step="0.01" min="0" value={r.importoDebito} onChange={(e) => updateRiga(i, "importoDebito", e.target.value)} placeholder="0,00" className={inputClass} />
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className={labelClass}>Credito (€)</label>
                        <input type="number" step="0.01" min="0" value={r.importoCredito} onChange={(e) => updateRiga(i, "importoCredito", e.target.value)} placeholder="0,00" className={inputClass} />
                      </div>
                      {righe.length > 1 && (
                        <button type="button" onClick={() => removeRiga(i)} aria-label="Rimuovi riga" className="mb-1 p-2 rounded-lg text-red-500 hover:bg-red-500/10">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  {errors.righe?.[i] && <p className="text-xs text-red-500 mt-1">{errors.righe[i]}</p>}
                </div>
              ))}
            </div>

            {/* Preset rapidi */}
            <div className="flex flex-wrap gap-2 mt-2">
              {PRESET_RIGHE.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => addRiga(p.riga)}
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${
                    darkMode ? "border-neutral-700 hover:bg-neutral-800 text-neutral-300" : "border-neutral-300 hover:bg-neutral-100 text-neutral-700"
                  }`}
                >
                  <Plus size={12} /> {p.label}
                </button>
              ))}
            </div>

            {/* Totale */}
            <div className={`flex justify-between items-center mt-3 pt-3 border-t ${darkMode ? "border-neutral-800" : "border-neutral-200"}`}>
              <span className={`text-sm font-semibold ${darkMode ? "text-neutral-300" : "text-neutral-700"}`}>Totale</span>
              <span className="text-lg font-bold text-forfy-600">{formatEuro(totale)}</span>
            </div>
            {errors.totale && <p className="text-xs text-red-500 mt-1">{errors.totale}</p>}
          </div>

          {/* Note */}
          <div>
            <label className={labelClass}>Note</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Note opzionali..." className={inputClass} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium ${darkMode ? "bg-neutral-800 hover:bg-neutral-700" : "bg-neutral-100 hover:bg-neutral-200"}`}>
              Annulla
            </button>
            <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium bg-forfy-500 text-white hover:bg-forfy-600">
              {mode === "edit" ? "Salva modifiche" : "Crea F24"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
